<template>
  <div class="wykazkierowcy-page">
    <div class="page-header">
      <div class="page-header-text">
        <h1 class="page-title">Wykaz kierowców</h1>
      </div>
    </div>
    <!-- City selector -->
    <v-row class="mb-2" align="center">
      <v-col cols="12" md="4">
        <v-select v-model="selectedSubCompanyId" :items="subCompanies" item-text="name" item-value="id" :label="$t('wykazKierowcy.cityBranch')" clearable dense outlined hide-details prepend-inner-icon="mdi-city"></v-select>
      </v-col>
    </v-row>

    <v-tabs v-model="activeTab" class="mb-4">
      <v-tab>{{ $t('wykazKierowcy.active') }} ({{ activeTotal }})</v-tab>
      <v-tab>{{ $t('wykazKierowcy.pending') }} <v-badge v-if="pendingCount > 0" :content="pendingCount" color="orange" inline></v-badge></v-tab>
      <v-tab>{{ $t('wykazKierowcy.delisted') }} ({{ delistedTotal }})</v-tab>
      <v-tab>{{ $t('wykazKierowcy.archive') }} ({{ archiveTotal }})</v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab" touchless>
      <!-- ACTIVE -->
      <v-tab-item>
        <v-data-table :headers="activeHeaders" :items="activeItems" :loading="loading" :options.sync="activeOptions" :server-items-length="activeTotal" :footer-props="{ 'items-per-page-options': [10, 25, 50] }" class="elevation-1">
          <template v-slot:top>
            <v-toolbar flat>
              <v-toolbar-title>{{ $t('wykaz.drivers.title') }}</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-text-field v-model="search" :label="$t('common.search') + '...'" single-line hide-details clearable class="mr-4" style="max-width:300px" prepend-inner-icon="mdi-magnify"></v-text-field>
              <v-btn color="blue darken-2" dark class="mr-2" @click="downloadExcel"><v-icon left>mdi-file-excel</v-icon>Excel</v-btn>
              <v-btn color="red darken-2" dark class="mr-2" @click="downloadPdf"><v-icon left>mdi-file-pdf-box</v-icon>PDF</v-btn>
<v-btn color="primary" dark @click="openCreateDialog"><v-icon left>mdi-plus</v-icon>{{ $t('wykaz.drivers.addButton') }}</v-btn>
            </v-toolbar>
          </template>
          <template v-slot:[`item.orzeczenieLekWaznosc`]="{ item }">
            <v-chip v-if="item.orzeczenieLekWaznosc" small :color="getChipColor(item.orzeczenieLekStatus)" dark>{{ formatDate(item.orzeczenieLekWaznosc) }}</v-chip>
            <span v-else>-</span>
          </template>
          <template v-slot:[`item.orzeczeniePsychWaznosc`]="{ item }">
            <v-chip v-if="item.orzeczeniePsychWaznosc" small :color="getChipColor(item.orzeczeniePsychStatus)" dark>{{ formatDate(item.orzeczeniePsychWaznosc) }}</v-chip>
            <span v-else>-</span>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-btn icon small color="primary" @click="openDetailDialog(item)"><v-icon small>mdi-eye</v-icon></v-btn>
            <v-btn icon small color="orange" @click="openEditDialog(item)"><v-icon small>mdi-pencil</v-icon></v-btn>
            <v-btn icon small color="deep-purple" @click="openDelistDialog(item)" :title="$t('wykazKierowcy.delistFromRegistry')"><v-icon small>mdi-account-remove</v-icon></v-btn>
            <v-btn icon small color="red" @click="confirmDeactivate(item)"><v-icon small>mdi-archive-arrow-down</v-icon></v-btn>
          </template>
        </v-data-table>
      </v-tab-item>

      <!-- PENDING -->
      <v-tab-item>
        <v-data-table :headers="pendingHeaders" :items="pendingItems" :loading="pendingLoading" :options.sync="pendingOptions" :server-items-length="pendingTotal" :footer-props="{ 'items-per-page-options': [10, 25, 50] }" class="elevation-1">
          <template v-slot:top><v-toolbar flat><v-toolbar-title>{{ $t('wykazKierowcy.pendingApproval') }}</v-toolbar-title></v-toolbar></template>
          <template v-slot:[`item.createdAt`]="{ item }">{{ formatDate(item.createdAt) }}</template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-btn icon small color="primary" @click="openDetailDialog(item)"><v-icon small>mdi-eye</v-icon></v-btn>
            <v-btn icon small color="orange" @click="openEditDialog(item)"><v-icon small>mdi-pencil</v-icon></v-btn>
            <v-btn small color="success" class="mr-1" @click="confirmApprove(item)"><v-icon left small>mdi-check</v-icon>{{ $t('common.approve') }}</v-btn>
            <v-btn small color="error" @click="openRejectDialog(item)"><v-icon left small>mdi-close</v-icon>{{ $t('common.reject') }}</v-btn>
          </template>
        </v-data-table>
      </v-tab-item>

      <!-- DELISTED -->
      <v-tab-item>
        <v-data-table :headers="delistedHeaders" :items="delistedItems" :loading="delistedLoading" :options.sync="delistedOptions" :server-items-length="delistedTotal" :footer-props="{ 'items-per-page-options': [10, 25, 50] }" class="elevation-1">
          <template v-slot:top><v-toolbar flat><v-toolbar-title>{{ $t('wykazKierowcy.delistedFromRegistry') }}</v-toolbar-title></v-toolbar></template>
          <template v-slot:[`item.delistedAt`]="{ item }">{{ formatDate(item.delistedAt) }}</template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-btn icon small color="primary" @click="openDetailDialog(item)"><v-icon small>mdi-eye</v-icon></v-btn>
          </template>
        </v-data-table>
      </v-tab-item>

      <!-- ARCHIVE -->
      <v-tab-item>
        <v-data-table :headers="archiveHeaders" :items="archiveItems" :loading="archiveLoading" :options.sync="archiveOptions" :server-items-length="archiveTotal" :footer-props="{ 'items-per-page-options': [10, 25, 50] }" class="elevation-1">
          <template v-slot:top><v-toolbar flat><v-toolbar-title>{{ $t('wykazKierowcy.driverArchive') }}</v-toolbar-title></v-toolbar></template>
          <template v-slot:[`item.dataDezaktywacji`]="{ item }">{{ formatDate(item.dataDezaktywacji) }}</template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-btn small color="success" @click="confirmRestore(item)"><v-icon left small>mdi-archive-arrow-up</v-icon>{{ $t('wykazKierowcy.restore') }}</v-btn>
          </template>
        </v-data-table>
      </v-tab-item>
    </v-tabs-items>

    <!-- CREATE/EDIT DIALOG -->
    <v-dialog content-class="dark-form-dialog" v-model="formDialog" max-width="800px">
      <v-card>
        <v-card-title>{{ editMode ? $t('wykazKierowcy.editDriver') : $t('wykazKierowcy.addNewDriver') }}</v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="formValid">
            <v-row>
              <v-col cols="12" md="4">
                <v-select v-model="formData.subCompanyId" :items="subCompanies" item-text="name" item-value="id" :label="$t('wykazKierowcy.cityBranch') + ' *'" :rules="[rules.required]" required></v-select>
              </v-col>
              <v-col cols="12" md="2" v-if="editMode">
                <v-text-field v-model.number="formData.lp" :label="$t('wykazKierowcy.lp')" type="number" outlined dense></v-text-field>
              </v-col>
              <v-col cols="12" :md="editMode ? 6 : 6">
                <v-text-field v-model="formData.imieNazwisko" :label="$t('wykazKierowcy.fullName') + ' *'" :rules="[rules.required]" required></v-text-field>
              </v-col>
              <v-col cols="12" md="6"><v-text-field v-model="formData.prawoJazdySeriaNr" :label="$t('wykazKierowcy.drivingLicense')"></v-text-field></v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6"><v-text-field v-model="formData.orzeczenieLekNr" :label="$t('wykazKierowcy.medicalCertNumber')"></v-text-field></v-col>
              <v-col cols="12" md="6">
                <v-menu v-model="dateMenuMed" :close-on-content-click="false" offset-y min-width="auto">
                  <template v-slot:activator="{ on, attrs }"><v-text-field v-model="formData.orzeczenieLekWaznosc" :label="$t('wykazKierowcy.medicalExpiry')" prepend-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" clearable></v-text-field></template>
                  <v-date-picker v-model="formData.orzeczenieLekWaznosc" @input="dateMenuMed = false" :locale="$i18n.locale"></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6"><v-text-field v-model="formData.orzeczeniePsychNr" :label="$t('wykazKierowcy.psychCertNumber')"></v-text-field></v-col>
              <v-col cols="12" md="6">
                <v-menu v-model="dateMenuPsych" :close-on-content-click="false" offset-y min-width="auto">
                  <template v-slot:activator="{ on, attrs }"><v-text-field v-model="formData.orzeczeniePsychWaznosc" :label="$t('wykazKierowcy.psychExpiry')" prepend-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" clearable></v-text-field></template>
                  <v-date-picker v-model="formData.orzeczeniePsychWaznosc" @input="dateMenuPsych = false" :locale="$i18n.locale"></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="4">
                <v-menu v-model="dateMenuCrim" :close-on-content-click="false" offset-y min-width="auto">
                  <template v-slot:activator="{ on, attrs }"><v-text-field v-model="formData.zaswiadczenieNiekaralnosci" :label="$t('wykazKierowcy.criminalRecord')" prepend-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" clearable></v-text-field></template>
                  <v-date-picker v-model="formData.zaswiadczenieNiekaralnosci" @input="dateMenuCrim = false" :locale="$i18n.locale"></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" md="4"><v-select v-model="formData.typLicencji" :items="licenseTypes" :label="$t('wykazKierowcy.licenseType')"></v-select></v-col>
              <v-col cols="12" md="4"><v-text-field v-model="formData.nrRejestracyjnyPojazdu" :label="$t('wykazKierowcy.vehiclePlate')"></v-text-field></v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="formDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" @click="submitForm" :disabled="!formValid">{{ editMode ? $t('common.save') : $t('common.add') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DETAIL DIALOG -->
    <v-dialog content-class="dark-form-dialog" v-model="detailDialog" max-width="650px">
      <v-card v-if="detailItem">
        <v-card-title>{{ detailItem.imieNazwisko }}</v-card-title>
        <v-card-text>
          <v-simple-table>
            <tbody>
              <tr><td class="font-weight-bold">{{ $t('wykazKierowcy.lp') }}</td><td>{{ detailItem.lp || '-' }}</td></tr>
              <tr><td class="font-weight-bold">{{ $t('wykazKierowcy.fullName') }}</td><td>{{ detailItem.imieNazwisko }}</td></tr>
              <tr><td class="font-weight-bold">{{ $t('wykazKierowcy.medicalCert') }}</td><td>Nr: {{ detailItem.orzeczenieLekNr || '-' }} <v-chip v-if="detailItem.orzeczenieLekWaznosc" small class="ml-2" :color="getChipColor(detailItem.orzeczenieLekStatus)" dark>do {{ formatDate(detailItem.orzeczenieLekWaznosc) }}</v-chip></td></tr>
              <tr><td class="font-weight-bold">{{ $t('wykazKierowcy.psychCert') }}</td><td>Nr: {{ detailItem.orzeczeniePsychNr || '-' }} <v-chip v-if="detailItem.orzeczeniePsychWaznosc" small class="ml-2" :color="getChipColor(detailItem.orzeczeniePsychStatus)" dark>do {{ formatDate(detailItem.orzeczeniePsychWaznosc) }}</v-chip></td></tr>
              <tr><td class="font-weight-bold">{{ $t('wykazKierowcy.drivingLicense') }}</td><td>{{ detailItem.prawoJazdySeriaNr || '-' }}</td></tr>
              <tr><td class="font-weight-bold">{{ $t('wykazKierowcy.criminalRecord') }}</td><td>{{ formatDate(detailItem.zaswiadczenieNiekaralnosci) }}</td></tr>
              <tr><td class="font-weight-bold">{{ $t('wykazKierowcy.licenseType') }}</td><td>{{ detailItem.typLicencji }}</td></tr>
              <tr><td class="font-weight-bold">{{ $t('wykazKierowcy.vehicle') }}</td><td>{{ detailItem.nrRejestracyjnyPojazdu || '-' }}</td></tr>
              <tr><td class="font-weight-bold">{{ $t('common.status') }}</td><td>{{ detailItem.status }}</td></tr>
              <tr v-if="detailItem.dodal"><td class="font-weight-bold">{{ $t('wykazKierowcy.addedBy') }}</td><td>{{ detailItem.dodal }}</td></tr>
              <tr v-if="detailItem.zatwierdzil"><td class="font-weight-bold">{{ $t('wykazKierowcy.approvedBy') }}</td><td>{{ detailItem.zatwierdzil }}</td></tr>
              <tr v-if="detailItem.powodOdrzucenia"><td class="font-weight-bold">{{ $t('wykazKierowcy.rejectionReason') }}</td><td class="red--text">{{ detailItem.powodOdrzucenia }}</td></tr>
              <tr v-if="detailItem.delistedAt"><td class="font-weight-bold">{{ $t('wykazKierowcy.delistedOn') }}</td><td class="deep-purple--text">{{ formatDate(detailItem.delistedAt) }}</td></tr>
              <tr v-if="detailItem.delistedBy"><td class="font-weight-bold">{{ $t('wykazKierowcy.delistedByPerson') }}</td><td>{{ detailItem.delistedBy }}</td></tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
        <v-card-actions><v-spacer></v-spacer><v-btn text @click="detailDialog = false">{{ $t('common.close') }}</v-btn></v-card-actions>
      </v-card>
    </v-dialog>

    <!-- REJECT DIALOG -->
    <v-dialog content-class="dark-form-dialog" v-model="rejectDialog" max-width="500px">
      <v-card>
        <v-card-title>{{ $t('wykazKierowcy.rejectEntry') }}</v-card-title>
        <v-card-text>
          <p>{{ $t('common.driver') }}: <b>{{ rejectItem?.imieNazwisko }}</b></p>
          <v-textarea v-model="rejectReason" :label="$t('wykazKierowcy.rejectionReason') + ' *'" outlined rows="3"></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="rejectDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="error" @click="submitReject" :disabled="!rejectReason">{{ $t('wykazKierowcy.rejectEntry') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DELIST DIALOG -->
    <v-dialog content-class="dark-form-dialog" v-model="delistDialog" max-width="500px">
      <v-card>
        <v-card-title>{{ $t('wykazKierowcy.delistTitle') }}</v-card-title>
        <v-card-text>
          <p>{{ $t('common.driver') }}: <b>{{ delistItem?.imieNazwisko }}</b></p>
          <v-menu v-model="dateMenuDelist" :close-on-content-click="false" offset-y min-width="auto">
            <template v-slot:activator="{ on, attrs }"><v-text-field v-model="delistDate" :label="$t('wykazKierowcy.delistDate') + ' *'" prepend-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" :rules="[rules.required]"></v-text-field></template>
            <v-date-picker v-model="delistDate" @input="dateMenuDelist = false" :locale="$i18n.locale"></v-date-picker>
          </v-menu>
          <v-text-field v-model="delistPerson" :label="$t('wykazKierowcy.delistedByPerson') + ' *'" :rules="[rules.required]"></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="delistDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="deep-purple" dark @click="submitDelist" :disabled="!delistDate || !delistPerson">{{ $t('wykazKierowcy.saveDelist') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Swal from "sweetalert2";
const axios = require("axios");

export default {
  name: "WykazKierowcyPage",
  data() {
    return {
      activeTab: 0,
      syncing: false,
      subCompanies: [], selectedSubCompanyId: null,
      activeItems: [], loading: false, activeTotal: 0, search: "",
      activeOptions: { page: 1, itemsPerPage: 10 },
      activeHeaders: [
        { text: "Lp.", value: "lp", width: "60px" },
        { text: "Imię i nazwisko", value: "imieNazwisko" },
        { text: "Prawo jazdy", value: "prawoJazdySeriaNr" },
        { text: "Orz. lek. ważne do", value: "orzeczenieLekWaznosc" },
        { text: "Orz. psych. ważne do", value: "orzeczeniePsychWaznosc" },
        { text: "Typ licencji", value: "typLicencji" },
        { text: "Nr rej. pojazdu", value: "nrRejestracyjnyPojazdu" },
        { text: "Akcje", value: "actions", sortable: false, width: "180px" },
      ],
      pendingItems: [], pendingLoading: false, pendingTotal: 0, pendingCount: 0,
      pendingOptions: { page: 1, itemsPerPage: 10 },
      pendingHeaders: [
        { text: "Imię i nazwisko", value: "imieNazwisko" },
        { text: "Nr rej. pojazdu", value: "nrRejestracyjnyPojazdu" },
        { text: "Dodał", value: "dodal" },
        { text: "Data dodania", value: "createdAt" },
        { text: "Akcje", value: "actions", sortable: false, width: "280px" },
      ],
      delistedItems: [], delistedLoading: false, delistedTotal: 0,
      delistedOptions: { page: 1, itemsPerPage: 10 },
      delistedHeaders: [
        { text: "Lp.", value: "lp", width: "60px" },
        { text: "Imię i nazwisko", value: "imieNazwisko" },
        { text: "Typ licencji", value: "typLicencji" },
        { text: "Data wykreślenia", value: "delistedAt" },
        { text: "Wykreślił(a)", value: "delistedBy" },
        { text: "Akcje", value: "actions", sortable: false, width: "80px" },
      ],
      archiveItems: [], archiveLoading: false, archiveTotal: 0,
      archiveOptions: { page: 1, itemsPerPage: 10 },
      archiveHeaders: [
        { text: "Lp. (oryg.)", value: "lp", width: "80px" },
        { text: "Imię i nazwisko", value: "imieNazwisko" },
        { text: "Typ licencji", value: "typLicencji" },
        { text: "Data dezaktywacji", value: "dataDezaktywacji" },
        { text: "Dezaktywował", value: "dezaktywowal" },
        { text: "Akcje", value: "actions", sortable: false, width: "140px" },
      ],
      formDialog: false, formValid: false, editMode: false, editId: null,
      dateMenuMed: false, dateMenuPsych: false, dateMenuCrim: false,
      licenseTypes: ["Osob/taxi", "Osobowa", "Taxi"],
      formData: { subCompanyId: "", lp: null, imieNazwisko: "", orzeczenieLekNr: "", orzeczenieLekWaznosc: "", orzeczeniePsychNr: "", orzeczeniePsychWaznosc: "", prawoJazdySeriaNr: "", zaswiadczenieNiekaralnosci: "", typLicencji: "Osob/taxi", nrRejestracyjnyPojazdu: "" },
      rules: { required: (v) => !!v || "Pole wymagane" },
      detailDialog: false, detailItem: null,
      rejectDialog: false, rejectItem: null, rejectReason: "",
      delistDialog: false, delistItem: null, delistDate: "", delistPerson: "", dateMenuDelist: false,
    };
  },
  watch: {
    activeOptions: { handler() { this.fetchActive(); }, deep: true },
    search() { this.activeOptions.page = 1; this.fetchActive(); },
    pendingOptions: { handler() { this.fetchPending(); }, deep: true },
    archiveOptions: { handler() { this.fetchArchive(); }, deep: true },
    delistedOptions: { handler() { this.fetchDelisted(); }, deep: true },
    activeTab(v) { if (v === 1) this.fetchPending(); if (v === 2) this.fetchDelisted(); if (v === 3) this.fetchArchive(); },
    selectedSubCompanyId() {
      this.activeOptions.page = 1; this.pendingOptions.page = 1; this.delistedOptions.page = 1; this.archiveOptions.page = 1;
      this.fetchActive(); this.fetchPendingCount();
      if (this.activeTab === 1) this.fetchPending();
      if (this.activeTab === 2) this.fetchDelisted();
      if (this.activeTab === 3) this.fetchArchive();
    },
  },
  async created() { await this.fetchSubCompanies(); await this.fetchActive(); await this.fetchPendingCount(); },
  methods: {
    async syncFromDrivers() {
      const confirm = await Swal.fire({
        title: "Synchronizuj z systemu",
        text: "Dodaj do wykazu wszystkich aktywnych kierowców i ich pojazdy, których jeszcze nie ma w wykazie.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Synchronizuj",
        cancelButtonText: "Anuluj",
      });
      if (!confirm.isConfirmed) return;
      this.syncing = true;
      try {
        const r = await axios({ method: "post", url: process.env.VUE_APP_ROOT_API + "registry/drivers/sync", headers: this.getHeaders() });
        const data = r.data.data;
        Swal.fire("Gotowe", `Dodano: ${data.created}, pominięto (już w wykazie): ${data.skipped}, łącznie kierowców: ${data.total}`, "success");
        this.fetchActive(); this.fetchPending(); this.fetchPendingCount();
      } catch (e) { Swal.fire("Błąd", e.response?.data?.message || "Błąd synchronizacji", "error"); }
      this.syncing = false;
    },
    getHeaders() { return { Authorization: "Bearer " + localStorage.getItem("token"), "x-current-page": "wykaz-kierowcy" }; },
    formatDate(d) { return d ? new Date(d).toLocaleDateString("pl-PL") : "-"; },
    getChipColor(status) { return status === "red" ? "error" : status === "yellow" ? "warning" : "success"; },
    buildParams(opts, extraSearch) {
      const p = { take: opts.itemsPerPage, skip: opts.page, search: extraSearch || "" };
      if (this.selectedSubCompanyId) p.subCompanyId = this.selectedSubCompanyId;
      if (opts.sortBy?.length) { p.sortBy = opts.sortBy[0]; p.sortOrder = opts.sortDesc?.[0] ? "desc" : "asc"; }
      return p;
    },
    async fetchSubCompanies() {
      try { const r = await axios({ method: "get", url: process.env.VUE_APP_ROOT_API + "registry/vehicles/sub-companies", headers: this.getHeaders() }); this.subCompanies = (r.data.data || []).filter(s => !s.deleted); } catch (e) { console.error(e); }
    },
    async fetchActive() {
      this.loading = true;
      try { const r = await axios({ method: "get", url: process.env.VUE_APP_ROOT_API + "registry/drivers", headers: this.getHeaders(), params: this.buildParams(this.activeOptions, this.search) }); this.activeItems = r.data.data.result; this.activeTotal = r.data.data.count; } catch (e) { console.error(e); }
      this.loading = false;
    },
    async fetchPending() {
      this.pendingLoading = true;
      try { const r = await axios({ method: "get", url: process.env.VUE_APP_ROOT_API + "registry/drivers/pending", headers: this.getHeaders(), params: this.buildParams(this.pendingOptions) }); this.pendingItems = r.data.data.result; this.pendingTotal = r.data.data.count; } catch (e) { console.error(e); }
      this.pendingLoading = false;
    },
    async fetchPendingCount() {
      try { const p = {}; if (this.selectedSubCompanyId) p.subCompanyId = this.selectedSubCompanyId; const r = await axios({ method: "get", url: process.env.VUE_APP_ROOT_API + "registry/drivers/pending/count", headers: this.getHeaders(), params: p }); this.pendingCount = r.data.data; } catch (e) { console.error(e); }
    },
    async fetchDelisted() {
      this.delistedLoading = true;
      try { const r = await axios({ method: "get", url: process.env.VUE_APP_ROOT_API + "registry/drivers/delisted", headers: this.getHeaders(), params: this.buildParams(this.delistedOptions) }); this.delistedItems = r.data.data.result; this.delistedTotal = r.data.data.count; } catch (e) { console.error(e); }
      this.delistedLoading = false;
    },
    async fetchArchive() {
      this.archiveLoading = true;
      try { const r = await axios({ method: "get", url: process.env.VUE_APP_ROOT_API + "registry/drivers/archive", headers: this.getHeaders(), params: this.buildParams(this.archiveOptions) }); this.archiveItems = r.data.data.result; this.archiveTotal = r.data.data.count; } catch (e) { console.error(e); }
      this.archiveLoading = false;
    },
    openCreateDialog() {
      this.editMode = false; this.editId = null;
      this.formData = { subCompanyId: this.selectedSubCompanyId || "", imieNazwisko: "", orzeczenieLekNr: "", orzeczenieLekWaznosc: "", orzeczeniePsychNr: "", orzeczeniePsychWaznosc: "", prawoJazdySeriaNr: "", zaswiadczenieNiekaralnosci: "", typLicencji: "Osob/taxi", nrRejestracyjnyPojazdu: "" };
      this.formDialog = true;
    },
    async openEditDialog(item) {
      this.editMode = true; this.editId = item.id;
      try {
        const r = await axios({ method: "get", url: process.env.VUE_APP_ROOT_API + "registry/drivers/" + item.id, headers: this.getHeaders() });
        const d = r.data.data;
        this.formData = { subCompanyId: d.subCompanyId || "", lp: d.lp || null, imieNazwisko: d.imieNazwisko, orzeczenieLekNr: d.orzeczenieLekNr || "", orzeczenieLekWaznosc: d.orzeczenieLekWaznosc ? d.orzeczenieLekWaznosc.substring(0,10) : "", orzeczeniePsychNr: d.orzeczeniePsychNr || "", orzeczeniePsychWaznosc: d.orzeczeniePsychWaznosc ? d.orzeczeniePsychWaznosc.substring(0,10) : "", prawoJazdySeriaNr: d.prawoJazdySeriaNr || "", zaswiadczenieNiekaralnosci: d.zaswiadczenieNiekaralnosci ? d.zaswiadczenieNiekaralnosci.substring(0,10) : "", typLicencji: d.typLicencji || "Osob/taxi", nrRejestracyjnyPojazdu: d.nrRejestracyjnyPojazdu || "" };
        this.formDialog = true;
      } catch (e) { Swal.fire("Błąd", "Nie udało się pobrać danych", "error"); }
    },
    async openDetailDialog(item) {
      try { const r = await axios({ method: "get", url: process.env.VUE_APP_ROOT_API + "registry/drivers/" + item.id, headers: this.getHeaders() }); this.detailItem = r.data.data; this.detailDialog = true; } catch (e) { console.error(e); }
    },
    cleanPayload() {
      const d = { ...this.formData };
      Object.keys(d).forEach(k => { if (d[k] === "") d[k] = null; });
      if (!d.imieNazwisko) d.imieNazwisko = this.formData.imieNazwisko;
      return d;
    },
    async submitForm() {
      if (!this.$refs.form.validate()) return;
      try {
        const payload = this.cleanPayload();
        if (this.editMode) { await axios({ method: "put", url: process.env.VUE_APP_ROOT_API + "registry/drivers/" + this.editId, headers: this.getHeaders(), data: payload }); }
        else { await axios({ method: "post", url: process.env.VUE_APP_ROOT_API + "registry/drivers", headers: this.getHeaders(), data: payload }); }
        Swal.fire("Sukces", this.editMode ? "Zaktualizowano" : "Dodano do kolejki", "success");
        this.formDialog = false; this.fetchActive(); this.fetchPending(); this.fetchPendingCount();
      } catch (e) { Swal.fire("Błąd", e.response?.data?.message || "Błąd", "error"); }
    },
    async confirmApprove(item) {
      const r = await Swal.fire({ title: "Zatwierdź kierowcę?", html: `<b>${item.imieNazwisko}</b> zostanie dodany do wykazu.`, icon: "question", showCancelButton: true, confirmButtonText: "Zatwierdź", cancelButtonText: "Anuluj" });
      if (!r.isConfirmed) return;
      try { await axios({ method: "post", url: process.env.VUE_APP_ROOT_API + "registry/drivers/" + item.id + "/approve", headers: this.getHeaders() }); Swal.fire("Zatwierdzono", "Kierowca w wykazie", "success"); this.fetchActive(); this.fetchPending(); this.fetchPendingCount(); }
      catch (e) { Swal.fire("Błąd", e.response?.data?.message || "Błąd", "error"); }
    },
    openRejectDialog(item) { this.rejectItem = item; this.rejectReason = ""; this.rejectDialog = true; },
    async submitReject() {
      if (!this.rejectReason) return;
      try { await axios({ method: "post", url: process.env.VUE_APP_ROOT_API + "registry/drivers/" + this.rejectItem.id + "/reject", headers: this.getHeaders(), data: { powodOdrzucenia: this.rejectReason } }); Swal.fire("Odrzucono", "Wpis odrzucony", "info"); this.rejectDialog = false; this.fetchPending(); this.fetchPendingCount(); }
      catch (e) { Swal.fire("Błąd", "Nie udało się odrzucić", "error"); }
    },
    openDelistDialog(item) { this.delistItem = item; this.delistDate = new Date().toISOString().substring(0, 10); this.delistPerson = ""; this.delistDialog = true; },
    async submitDelist() {
      if (!this.delistDate || !this.delistPerson) return;
      try { await axios({ method: "post", url: process.env.VUE_APP_ROOT_API + "registry/drivers/" + this.delistItem.id + "/delist", headers: this.getHeaders(), data: { delistedAt: this.delistDate, delistedBy: this.delistPerson } }); Swal.fire("Wykreślono", "Kierowca wykreślony z wykazu", "success"); this.delistDialog = false; this.fetchActive(); this.fetchDelisted(); }
      catch (e) { Swal.fire("Błąd", e.response?.data?.message || "Błąd wykreślenia", "error"); }
    },
    async confirmDeactivate(item) {
      const r = await Swal.fire({ title: "Dezaktywuj kierowcę?", html: `<b>${item.imieNazwisko}</b> zostanie przeniesiony do archiwum.`, icon: "warning", showCancelButton: true, confirmButtonColor: "#d33", confirmButtonText: "Dezaktywuj", cancelButtonText: "Anuluj" });
      if (!r.isConfirmed) return;
      try { await axios({ method: "post", url: process.env.VUE_APP_ROOT_API + "registry/drivers/" + item.id + "/deactivate", headers: this.getHeaders() }); Swal.fire("Dezaktywowano", "W archiwum", "success"); this.fetchActive(); this.fetchArchive(); }
      catch (e) { Swal.fire("Błąd", "Błąd dezaktywacji", "error"); }
    },
    async confirmRestore(item) {
      const r = await Swal.fire({ title: "Przywróć kierowcę?", html: `Przywrócić <b>${item.imieNazwisko}</b>?`, icon: "question", showCancelButton: true, confirmButtonText: "Przywróć", cancelButtonText: "Anuluj" });
      if (!r.isConfirmed) return;
      try { await axios({ method: "post", url: process.env.VUE_APP_ROOT_API + "registry/drivers/archive/" + item.id + "/restore", headers: this.getHeaders() }); Swal.fire("Przywrócono", "W wykazie", "success"); this.fetchActive(); this.fetchArchive(); }
      catch (e) { Swal.fire("Błąd", "Błąd przywracania", "error"); }
    },
    async downloadExcel() {
      try { const p = {}; if (this.selectedSubCompanyId) p.subCompanyId = this.selectedSubCompanyId; const r = await axios({ method: "get", url: process.env.VUE_APP_ROOT_API + "registry/drivers/export/excel", headers: this.getHeaders(), params: p, responseType: "blob" }); const u = window.URL.createObjectURL(new Blob([r.data])); const a = document.createElement("a"); a.href = u; a.download = "wykaz_kierowcow.xlsx"; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(u); }
      catch (e) { Swal.fire("Błąd", "Błąd Excel", "error"); }
    },
    async downloadPdf() {
      try { const p = {}; if (this.selectedSubCompanyId) p.subCompanyId = this.selectedSubCompanyId; const r = await axios({ method: "get", url: process.env.VUE_APP_ROOT_API + "registry/drivers/export/pdf", headers: this.getHeaders(), params: p, responseType: "blob" }); const u = window.URL.createObjectURL(new Blob([r.data])); const a = document.createElement("a"); a.href = u; a.download = "wykaz_kierowcow.pdf"; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(u); }
      catch (e) { Swal.fire("Błąd", "Błąd PDF", "error"); }
    },
  },
};
</script>

<style scoped>
.wykazkierowcy-page { background: #181C28; min-height: calc(100vh - 56px); padding: 16px 16px 40px; margin: -24px; color: #fff; }
.page-header { padding: 4px 4px 18px; }
.page-eyebrow { font-size: 12px; color: #6E7691; font-weight: 500; }
.page-title { font-size: 22px; font-weight: 800; color: #fff; margin: 2px 0 0; letter-spacing: -0.02em; }
.wykazkierowcy-page >>> .v-card { background: #232838 !important; border: 1px solid rgba(255,255,255,0.06) !important; border-radius: 18px !important; box-shadow: none !important; color: #fff !important; }
.wykazkierowcy-page >>> .v-card .v-card__title { color: #fff !important; padding: 16px 20px 8px !important; font-weight: 700 !important; font-size: 15px !important; }
.wykazkierowcy-page >>> .v-text-field--outlined fieldset, .wykazkierowcy-page >>> .v-select--outlined fieldset { border-color: rgba(255,255,255,0.1) !important; }
.wykazkierowcy-page >>> .v-text-field--outlined .v-input__slot, .wykazkierowcy-page >>> .v-select--outlined .v-input__slot { background: #181C28 !important; border-radius: 12px !important; }
.wykazkierowcy-page >>> .v-label { color: #6E7691 !important; }
.wykazkierowcy-page >>> .v-text-field input, .wykazkierowcy-page >>> .v-select__selection { color: #fff !important; }
.wykazkierowcy-page >>> .v-data-table { background: #232838 !important; border: 1px solid rgba(255,255,255,0.06) !important; border-radius: 18px !important; color: #fff !important; box-shadow: none !important; }
.wykazkierowcy-page >>> .v-data-table thead tr th { background: #1F2434 !important; color: #6E7691 !important; font-size: 11px !important; font-weight: 700 !important; text-transform: uppercase; letter-spacing: 0.04em; border-bottom: 1px solid rgba(255,255,255,0.06) !important; }
.wykazkierowcy-page >>> .v-data-table tbody tr { background: #232838 !important; }
.wykazkierowcy-page >>> .v-data-table tbody tr:hover { background: #2A3042 !important; }
.wykazkierowcy-page >>> .v-data-table tbody tr td { color: #fff !important; border-bottom: 1px solid rgba(255,255,255,0.04) !important; }
.wykazkierowcy-page >>> .v-data-footer { background: #1F2434 !important; color: #B0BEC5 !important; border-top: 1px solid rgba(255,255,255,0.06) !important; }
.wykazkierowcy-page >>> .v-toolbar { background: transparent !important; color: #fff !important; }
.wykazkierowcy-page >>> .v-tabs { background: transparent !important; }
.wykazkierowcy-page >>> .v-tab { color: #B0BEC5 !important; text-transform: none !important; letter-spacing: -0.01em !important; font-weight: 600 !important; }
.wykazkierowcy-page >>> .v-tab--active { color: #5478FF !important; }
.wykazkierowcy-page >>> .v-tabs-bar { background: transparent !important; }
.wykazkierowcy-page >>> .v-tabs-slider { background: #5478FF !important; }
.wykazkierowcy-page >>> .v-btn:not(.v-btn--icon) { border-radius: 10px !important; text-transform: none !important; font-weight: 600 !important; }
.wykazkierowcy-page >>> .v-chip { border-radius: 999px !important; font-weight: 700 !important; }
.wykazkierowcy-page >>> .v-divider { border-color: rgba(255,255,255,0.06) !important; }
.wykazkierowcy-page >>> .v-progress-linear { background: rgba(255,255,255,0.06) !important; }
.wykazkierowcy-page >>> .v-dialog .v-card { background: #232838 !important; }
.wykazkierowcy-page >>> .v-tabs-items, .wykazkierowcy-page >>> .v-window { background: transparent !important; }
</style>
